define('presentation/receptionist/viewModels/receptionistSidebarViewModel',[
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'constants/colorConstants',
    'presentation/receptionist/dataSources/messageSubscriptionDataSource',
    'presentation/receptionist/facades/receptionistSidebarFacade',
    'presentation/receptionist/viewModels/receptionistViewModel'
], function () {
    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    const SorterConstructor = require('common/collections/collectionSorter');

    const _colorConstants = require('constants/colorConstants');
    const _commonState = require('common/storage/commonState');
    const _receptionistViewModel = require('presentation/receptionist/viewModels/receptionistViewModel');
    const _messageSubscriptionDataSource = require('presentation/receptionist/dataSources/messageSubscriptionDataSource');

    let _disposables = [];
    let _facade = null;
    let _hasScreenBeenInitialized = false;

    let _availableSubscriptions = _messageSubscriptionDataSource.subscriptions;

    let isDataLoaded = ko.observable(false);

    const _initializeScreen = () => {
        if (_hasScreenBeenInitialized) {
            return;
        }
        _hasScreenBeenInitialized = true;

        return _promiseFactory.defer((promise) => {
            _initializeFilters()
                .fail(promise.reject)
                .done(() => {
                    _initializeSubscriptions()
                        .fail(promise.reject)
                        .done(() => {
                            isDataLoaded(true);
                            _initializeMessages()
                                .fail(promise.reject)
                                .done(promise.resolve);
                        });
                });
        });
    };

    const _initializeFilters = () => {
        return _promiseFactory.defer((promise) => {
            _facade.getSidebarSettings()
                .fail(promise.reject)
                .done((settings) => {
                    searchFilter('');
                    selectedShowFilter(settings.selectedShowFilter);
                    selectedInteractionFilter(settings.selectedInteractionFilter);
                    shouldDisplayJunkMessages(settings.isIncludeJunk);
                    selectedTimeframeFilter(settings.selectedTimeframeFilter);
                    startOn(settings.timeframeCustomStartOn);
                    endOn(settings.timeframeCustomEndOn);

                    promise.resolve();
                });
        });
    };

    const _initializeSubscriptions = () => {
        return _promiseFactory.defer((promise) => {
            _messageSubscriptionDataSource.waitForDataWaitHandle.waitForSignal(() => {
                promise.resolve();
            });
        });
    };

    const _initializeMessages = () => {
        return _promiseFactory.defer((promise) => {
            if (isDataLoaded() === false) {
                promise.resolve();
                return;
            }

            _receptionistViewModel.refreshMessageListing()
                .fail(promise.reject)
                .done(promise.resolve);
        });
    };

    const _saveSidebarSettings = () => {
        const sidebarSettings = {
            selectedShowFilter: selectedShowFilter(),
            selectedInteractionFilter: selectedInteractionFilter(),
            isIncludeJunk: shouldDisplayJunkMessages(),
            selectedTimeframeFilter: selectedTimeframeFilter(),
            timeframeCustomStartOn: startOn(),
            timeframeCustomEndOn: endOn(),
        };
        return _facade.saveSidebarSettings(sidebarSettings);
    };

    const _onSearchCriteriaChanged = () => {
        _saveSidebarSettings().done(() =>  _initializeMessages());
    };

    const _onSearchTextChanged = () =>  {
        let searchText = searchFilter();
        _receptionistViewModel.searchTextChanged(searchText);
    };

    const _onSelectedTimeFilterChanged = () => {
        if (selectedTimeframeFilter() === '') {
            startOn('');
            endOn('');
        }
    };

    const clearFilters = () => {
        return _promiseFactory.deferWithMinimumWait((promise) => {
            selectedShowFilter('all');
            shouldDisplayJunkMessages(false);
            selectedInteractionFilter('all');
            selectedTimeframeFilter('');
            startOn('');
            endOn('');
            selectedSubscriptions([]);

            promise.resolve();
        });
    };

    const getSearchOptions = () => {
        let selectedLiveReceptionistScriptIds = [];

        if (selectedSubscriptions().length > 0) {
            selectedSubscriptions().forEach((subscription) => {
                selectedLiveReceptionistScriptIds.push(subscription.liveReceptionistScriptId);
            });
        } else {
            let options = subscriptionOptions();
            options.forEach((option) => {
                selectedLiveReceptionistScriptIds.push(option.liveReceptionistScriptId);
            });
        }

        let startDate = startOn();
        let endDate = endOn();
        let searchText = searchFilter();
        let searchStatus = selectedShowFilter();
        let includeJunk = shouldDisplayJunkMessages();
        let searchInteraction = selectedInteractionFilter();

        return {
            startDate,
            endDate,
            searchText,
            searchStatus,
            includeJunk,
            searchInteraction,
            selectedLiveReceptionistScriptIds,
        };
    };

    const longClickSpinnerColor = _colorConstants.colorSecondaryCool200;

    const searchFilter = ko.observable('').extend({throttle: 300});
    _disposables.push(searchFilter.subscribe(_onSearchTextChanged));

    const startOn = ko.observable('');
    _disposables.push(startOn.subscribe(_onSearchCriteriaChanged));

    const endOn = ko.observable('');
    _disposables.push(endOn.subscribe(_onSearchCriteriaChanged));

    const selectedShowFilter = ko.observable('');
    _disposables.push(selectedShowFilter.subscribe(_onSearchCriteriaChanged));

    const selectedInteractionFilter = ko.observable('');
    _disposables.push(selectedInteractionFilter.subscribe(_onSearchCriteriaChanged));

    const shouldDisplayJunkMessages =  ko.observable(false);
    _disposables.push(shouldDisplayJunkMessages.subscribe(_onSearchCriteriaChanged));

    const selectedTimeframeFilter = ko.observable('');
    _disposables.push(selectedTimeframeFilter.subscribe(_onSelectedTimeFilterChanged));

    const showFilterOptions = [
        {title: 'All', value: 'all'},
        {title: 'New', value: 'new'},
        {title: 'Reviewed', value: 'reviewed'}
    ];

    const junkFilterOptions = [
        {title: 'All', value: 'all'},
        {title: 'Abandoned', value: 'abandoned'},
        {title: 'Hangup', value: 'hangup'},
        {title: 'Spam', value: 'spam'},
        {title: 'External Website', value: 'externalWebsite'}
    ];

    const interactionFilterOptions = [
        {title: 'All', value: 'all'},
        {title: 'Calls', value: 'call'},
        {title: 'Web Chats', value: 'chat'}
    ];

    const subscriptionOptions = ko.computed(() => {
        if (isDataLoaded() === false) {
            return [];
        }
        const availableSubscriptions = _availableSubscriptions();
        if (availableSubscriptions.length === 0) {
            return [];
        }

        const liveReceptionistScripts = availableSubscriptions
            .map(script => {
                const iconProperty = {iconProperty: _commonState.types.liveReceptionistScript};
                return Object.assign({}, script, iconProperty);
            });
        const sorter = new SorterConstructor();
        sorter.sort(liveReceptionistScripts, "displayName");
        return liveReceptionistScripts;
    });

    const areSubscriptionOptionsAvailable = ko.computed(() => {
        return subscriptionOptions().length > 0;
    });

    const selectedSubscriptions = ko.observableArray([]).extend({observeState: true});
    _disposables.push(selectedSubscriptions.subscribe(_onSearchCriteriaChanged));

    const isClearFiltersButtonVisible = ko.computed(() => {
        return (selectedShowFilter() !== '' && selectedShowFilter() !== 'all') ||
            (selectedInteractionFilter() !== '' && selectedInteractionFilter() !== 'all') ||
            selectedTimeframeFilter() !== '' ||
            selectedSubscriptions().length > 0;
    });

    const filterAvailableSubscriptions = (allSubscriptions, selectedSubscriptions) => {
        const sorter = new SorterConstructor();
        sorter.sort(allSubscriptions, "displayName", true);
        return allSubscriptions.filter((subscription) => {
            return undefined === selectedSubscriptions.find((selectedSubscription) => {
                return subscription.liveReceptionistScriptId === selectedSubscription().liveReceptionistScriptId;
            });
        });
    };

    const _detached = () => {
        _disposables.forEach(s => s.dispose);
        _disposables = [];
    };

    const _initialize = () => {
        const FacadeConstructor = require('presentation/receptionist/facades/receptionistSidebarFacade');
        _facade = new FacadeConstructor();
        _facade.init(_promiseFactory);

        return _promiseFactory.wait();
    };

    const onLogin = () => {
        // Nothing to do
    };

    const onLogout = () => {
        _hasScreenBeenInitialized = false;
    };

    const sidebar = {
        clearFilters,
        getSearchOptions,
        filterAvailableSubscriptions,

        longClickSpinnerColor,
        isClearFiltersButtonVisible,
        searchFilter,
        startOn,
        endOn,
        selectedShowFilter,
        selectedInteractionFilter,
        shouldDisplayJunkMessages,
        selectedSubscriptions,
        showFilterOptions,
        junkFilterOptions,
        interactionFilterOptions,
        subscriptionOptions,
        areSubscriptionOptionsAvailable,
        isDataLoaded,
        selectedTimeframeFilter,

        // Durandal Events
        attached : _initializeScreen,
        activate : _initialize,
        detached : _detached,

        alerts : {
            receiveAlertLoginSuccessful : onLogin,
            receiveAlertLogout : onLogout,
        },
    };

    _receptionistViewModel.setSidebar(sidebar);
    return sidebar;
});
